import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import CommentPost from 'flarum/forum/components/CommentPost';
import PostControls from 'flarum/forum/utils/PostControls';
import LinkButton from 'flarum/common/components/LinkButton';
import Button from 'flarum/common/components/Button';
import Model from 'flarum/common/Model';

import CalendarPage from './components/CalendarPage';
import EventDiscussionHero from './components/EventDiscussionHero';
import EditEventModal from './components/EditEventModal';
import InterestedList from './components/InterestedList';
import Event from './models/Event';

app.initializers.add('kemze-calendar', () => {
	app.routes.advevents = { path: '/events', component: CalendarPage };
	app.store.models.events = Event;

	app.store.models.users.prototype.canModerateEvents = Model.attribute('canModerateEvents');
	app.store.models.users.prototype.canCreateEvents   = Model.attribute('canCreateEvents');
	app.store.models.users.prototype.canViewEvents     = Model.attribute('canViewEvents');

	extend(IndexPage.prototype, 'navItems', function (items) {
		items.add(
			'events',
			<LinkButton icon="fas fa-calendar-alt" href={app.route('advevents')}>
			{app.translator.trans('kemze-calendar.forum.button.landing')}
			</LinkButton>,
			50
		);
	});

	// ── Event record cache: discussionId -> Event | null | 'loading' ──────────
	const eventCache = {};

	// ── Burger menu: "Add Event" on first post ────────────────────────────────
	extend(PostControls, 'moderationControls', function (items, post) {
		if (!post || post.number() !== 1) return;
		if (!app.session.user?.canCreateEvents()) return;

		const discussion   = post.discussion();
		if (!discussion) return;
		const discussionId = discussion.id();

		// If already an event, don't show "Add Event"
		if (eventCache[discussionId]) return;

		items.add(
			'addEvent',
			<Button
			icon="fas fa-calendar-plus"
			onclick={() => {
				// Create the event record first, then open the edit modal
				app.store.createRecord('events').save({
					discussion_id: discussionId,
				}).then((event) => {
					eventCache[discussionId] = event;
					app.modal.show(EditEventModal, {
						event,
						onSave: (updated) => {
							eventCache[discussionId] = updated;
							m.redraw();
						},
					});
					m.redraw();
				});
			}}
			>
			{app.translator.trans('kemze-calendar.forum.event.add_event')}
			</Button>
		);
	});

	// ── Inject EventDiscussionHero into first post content ────────────────────
	extend(CommentPost.prototype, 'content', function (content) {
		const post = this.attrs.post;
		if (!post || post.number() !== 1) return;

		const discussion = post.discussion();
		if (!discussion) return;

		const discussionId = discussion.id();

		if (eventCache[discussionId] === 'loading') return;

		if (eventCache[discussionId] === undefined) {
			eventCache[discussionId] = 'loading';

			app.request({
				method: 'GET',
				url: `${app.forum.attribute('apiUrl')}/events-by-discussion`,
				params: { discussion_id: discussionId },
			}).then((response) => {
				if (response?.data?.length > 0) {
					const hydrated = app.store.pushPayload(response);
					eventCache[discussionId] = Array.isArray(hydrated) ? hydrated[0] : hydrated;
				} else {
					eventCache[discussionId] = null;
				}
				m.redraw();
			}).catch(() => {
				eventCache[discussionId] = null;
			});

			return;
		}

		if (!eventCache[discussionId]) return;

		content.push(
			<EventDiscussionHero
			event={eventCache[discussionId]}
			discussion={discussion}
			post={post}
			onEventSaved={(updated) => {
				eventCache[discussionId] = updated;
				m.redraw();
			}}
			/>
		);
	});

	// ── Remove standard likes display on event first posts ────────────────────
	extend(CommentPost.prototype, 'footerItems', function (items) {
		const post = this.attrs.post;
		if (!post || post.number() !== 1) return;

		const discussion = post.discussion();
		if (!discussion) return;

		const cached = eventCache[discussion.id()];
		if (cached && cached !== 'loading') {
			items.remove('liked');
		}
	});
	extend(CommentPost.prototype, 'actionItems', function (items) {
		const post = this.attrs.post;
		if (!post || post.number() !== 1) return;

		const discussion = post.discussion();
		if (!discussion) return;

		const cached = eventCache[discussion.id()];
		if (cached && cached !== 'loading') {
			items.remove('like');
		}
	});
});

export * from './components';
export * from './models';
export * from './utils';
